import React from 'react';
import { Table, Button, Space, Popconfirm, Tag, Typography, Tooltip } from 'antd';
import { SettingOutlined, DeleteOutlined } from '@ant-design/icons';
import { useAuth } from '../../auth/authcontext';
import { AttractorLoopListDto } from '../../types/attractortypes';

const { Text } = Typography;

export interface AttractorTableProps {
    data: AttractorLoopListDto[];
    loading: boolean;
    currentPage: number;
    pageSize: number;
    totalCount: number;
    onTableChange: (pagination: any) => void;
    onEdit: (guid: string) => void;
    onDelete: (guid: string) => void;
}

const AttractorTable: React.FC<AttractorTableProps> = ({
    data,
    loading,
    currentPage,
    pageSize,
    totalCount,
    onTableChange,
    onEdit,
    onDelete
}) => {
    const { isReadOnly } = useAuth();
    const columns = [
        {
            title: 'Loop Name',
            dataIndex: 'name',
            key: 'name',
            render: (text: string) => <Text strong style={{ fontSize: '15px' }}>{text}</Text>
        },
        {
            title: 'Status',
            dataIndex: 'active',
            key: 'active',
            render: (active: boolean) => (
                <Tag color={active ? 'success' : 'default'} style={{ fontSize: '13px', padding: '2px 8px' }}>
                    {active ? 'Active' : 'Inactive'}
                </Tag>
            )
        },
        {
            title: 'Stores Assigned',
            dataIndex: 'storeCount',
            key: 'storeCount',
            render: (count: number) => (
                <Tag color="cyan" style={{ fontSize: '13px', fontWeight: 600 }}>
                    {count} {count === 1 ? 'Store' : 'Stores'}
                </Tag>
            )
        },
        {
            title: 'Assigned Store List',
            dataIndex: 'assignedStoreNumbers',
            key: 'assignedStoreNumbers',
            render: (stores: string[]) => {
                if (!stores || stores.length === 0) {
                    return <Text type="secondary" italic style={{ fontSize: '12px' }}>Unassigned</Text>;
                }
                const displayStores = stores.slice(0, 3);
                const hiddenCount = stores.length - 3;
                return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', maxWidth: '300px' }}>
                        {displayStores.map((storeNum: string) => (
                            <Tag key={storeNum} color="blue" style={{ margin: 0 }}>
                                #{storeNum}
                            </Tag>
                        ))}
                        {hiddenCount > 0 && (
                            <Tooltip
                                title={
                                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                        {stores.slice(3).map((s: string) => `#${s}`).join(', ')}
                                    </div>
                                }
                                overlayInnerStyle={{ maxHeight: '200px', overflowY: 'auto' }}
                            >
                                <Tag color="default" style={{ margin: 0, cursor: 'pointer' }}>
                                    + {hiddenCount} more
                                </Tag>
                            </Tooltip>
                        )}
                    </div>
                );
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: AttractorLoopListDto) => (
                <Space size="middle">
                    <Button
                        type="primary"
                        ghost
                        icon={<SettingOutlined />}
                        onClick={() => onEdit(record.guid)}
                    >
                        Configure Loop
                    </Button>
                    {!isReadOnly && (
                        <Popconfirm
                            title="Delete Attractor Loop"
                            description="Are you sure you want to delete this attractor loop? Stores referencing this loop will fall back to default branding."
                            okText="Yes"
                            cancelText="No"
                            onConfirm={() => onDelete(record.guid)}
                        >
                            <Button type="primary" danger ghost icon={<DeleteOutlined />} />
                        </Popconfirm>
                    )}
                </Space>
            )
        }
    ];

    return (
        <Table
            columns={columns}
            dataSource={data.map(l => ({ ...l, key: l.guid }))}
            pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalCount,
                showSizeChanger: true,
                pageSizeOptions: ['5', '10', '20', '50'],
                showTotal: (total, range) => `Showing ${range[0]}–${range[1]} of ${total} loops`
            }}
            onChange={onTableChange}
            bordered
            loading={loading}
            style={{ borderRadius: '8px', overflow: 'hidden' }}
        />
    );
};

export default AttractorTable;
